// Considered: forking `SphereTypeComposerOrganism`'s own header/index/rail wiring for a second
// entity type — rejected (spec Reuse Audit, Phase 4): the whole point of Phase 1/2's extraction was
// that `EditorRailShellOrganism`, `ComposerIndexRailMolecule` and `InspectorRailOrganism` no longer
// know what they're editing. This organism proves that by mounting all three under an `AspectRow`
// instead of a `Schema`, with placeholder content only — it is not the Aspect Editor itself (that
// is a separate future spec), it is the generic-boundary proof Phase 4 asks for.
//
// `locked` is hardcoded `true` rather than derived from anything on `AspectRow`: the aspect read has
// no server-computed `canEdit` field yet (permissions item 7), and inferring one client-side here
// would be exactly the client inference that item forbids. This organism flips to a real value once
// that field exists on the read, not before.

import React from 'react';
import { useTranslation } from 'localization';
import type { AspectRow } from 'types/aspect';
import EditorRailShellOrganism from '../EditorRailShell/EditorRailShellOrganism';
import ComposerIndexRailMolecule, {
  type ComposerIndexRailItem,
} from '../../molecules/ComposerIndexRail/ComposerIndexRailMolecule';
import InspectorRailOrganism, { type ComposerSideRailHandle } from '../InspectorRail/InspectorRailOrganism';
import type { AgentThreadParity } from '../composer/AgentThreadOrganism';
import EditorBlockMolecule from '../../molecules/EditorBlock/EditorBlockMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './AspectEditorRailStubOrganism.scss';

export interface AspectEditorRailStubOrganismProps {
  aspect: AspectRow;
  onBack?: () => void;
}

/** The placeholder document's own blocks. Neither carries real content — that is the Aspect
 * Editor's own future spec; this stub only proves the shell/index/rail shape a second content
 * domain would build against. */
const PLACEHOLDER_BLOCK_IDS = ['overview', 'definition'] as const;

/** Inert — `agentTarget` is never passed, so `InspectorRailOrganism` never renders the Agent tab or
 * reads these ids, but the prop is required regardless of whether the tab is reachable. */
const ASPECT_SIDE_RAIL_AGENT_PARITY: AgentThreadParity = {
  threadContainer: 'side > panel#2 > list#2',
  itemContainer: 'side > panel#2 > list#2 > each(t.interview)',
  jump: 'side.see-it',
  shared: 'side.asked-once-for-both',
  composeContainer: 'side > panel#2 > group',
  input: 'side.tell-it-what-to-change',
  send: 'side.send',
  critiqueCard: 'side.watching-as-you-edit',
  critiqueHeading: 'side.watching-as-you-edit#2',
  critiqueChip: 'side.chip',
  critiqueDismiss: 'side.button#6',
};

const anchorId = (sectionId: string) => `aspect-editor-rail-section-${sectionId}`;

/**
 * Phase 4 (EAINT-921): mounts the generic editor-rail shell, index rail and inspector rail with an
 * `AspectRow` entity and placeholder blocks only. Renders zero `studio.typeEditor.*` keys and
 * imports no `Schema` type — see the file-level comment.
 */
const AspectEditorRailStubOrganism: React.FC<AspectEditorRailStubOrganismProps> = ({ aspect, onBack }) => {
  const { t } = useTranslation();
  const railRef = React.useRef<ComposerSideRailHandle>(null);
  const locked = true;

  const items: ComposerIndexRailItem[] = PLACEHOLDER_BLOCK_IDS.map((id) => ({
    id,
    icon: id === 'overview' ? 'pi pi-info-circle' : 'pi pi-sitemap',
    label: t(`aspectEditorRail.blocks.${id}.label`),
    completion: 'empty',
  }));

  return (
    <EditorRailShellOrganism
      altitude="type"
      locked={locked}
      header={
        <div className="aspect-editor-rail-stub__header" data-testid="aspect-editor-rail-stub-header">
          {onBack && (
            <ButtonAtom
              variant="text"
              shape="icon-square"
              size="small"
              icon="pi pi-arrow-left"
              onClick={onBack}
              aria-label={t('aspectEditorRail.back')}
              data-testid="aspect-editor-rail-stub-back"
            />
          )}
          <h1 className="aspect-editor-rail-stub__title">{aspect.name?.default ?? aspect.system_name}</h1>
        </div>
      }
      index={
        <ComposerIndexRailMolecule
          items={items}
          anchorId={anchorId}
          locked={locked}
          onInheritedClick={() => railRef.current?.focusInheritedTab()}
          namespace="aspectEditorRail.indexRail"
          ariaLabelKey="aspectEditorRail.sectionIndexLabel"
        />
      }
      rail={
        <InspectorRailOrganism
          ref={railRef}
          locked={locked}
          namespace="aspectEditorRail.sideRail"
          agentNamespace="aspectEditorRail.agent"
          parity={ASPECT_SIDE_RAIL_AGENT_PARITY}
          entity={{
            kind: 'aspect',
            id: aspect.id,
            systemName: aspect.system_name,
            label: aspect.name?.default ?? aspect.system_name,
          }}
        />
      }
    >
      {PLACEHOLDER_BLOCK_IDS.map((id) => (
        <EditorBlockMolecule
          key={id}
          anchorId={anchorId(id)}
          sectionId={id}
          icon={id === 'overview' ? 'pi pi-info-circle' : 'pi pi-sitemap'}
          label={t(`aspectEditorRail.blocks.${id}.label`)}
          completion="empty"
          bodyText={t('aspectEditorRail.blocks.placeholderNote')}
        >
          <div />
        </EditorBlockMolecule>
      ))}
    </EditorRailShellOrganism>
  );
};

AspectEditorRailStubOrganism.displayName = 'AspectEditorRailStubOrganism';

export default AspectEditorRailStubOrganism;
